import { DetectionSummary } from '@investigator/shared/generated/gql';
import { DetectionEntitySummary } from '../context';

/**
 * Validates whether an entity has sufficient alert context for suppression.
 * Suppression requires knowing which specific alert to suppress the entity for.
 *
 * @param detectionData - The detection/alert context data
 * @returns Object with validation results and missing fields
 */
export interface SuppressionValidation {
  /** Whether all required fields are present and valid */
  isValid: boolean;
  /** List of missing or invalid required fields */
  missingFields: string[];
  /** Human-readable reason if invalid */
  reason?: string;
}

export function validateSuppressionContext(
  detectionData?: DetectionSummary | DetectionEntitySummary | null,
): SuppressionValidation {
  if (!detectionData) {
    return {
      isValid: false,
      missingFields: ['content_id', 'alert_name', 'alert_type'],
      reason: 'No detection context available',
    };
  }

  const requiredFields: Array<keyof typeof detectionData> = [
    'content_id',
    'alert_name',
    'alert_type',
  ];

  const missingFields = requiredFields.filter((field) => {
    const value = detectionData[field];
    // Check for null, undefined, or empty string
    return !value || (typeof value === 'string' && value.trim() === '');
  });

  if (missingFields.length > 0) {
    return {
      isValid: false,
      missingFields: missingFields as string[],
      reason: `Missing required alert context: ${missingFields.join(', ')}`,
    };
  }

  return {
    isValid: true,
    missingFields: [],
  };
}

/**
 * Determines if suppression should be available for an entity.
 *
 * @param entityCategory - Whether entity is source or destination
 * @param detectionData - The detection/alert context data
 * @param requireSingleDetectionForSuppression - If true, only allow suppression when entity has exactly 1 detection
 * @param detectionCount - Number of detections for conditional logic
 * @returns Whether to show suppression option
 */
export function shouldShowSuppression(
  entityCategory?: 'source' | 'destination',
  detectionData?: DetectionSummary | DetectionEntitySummary | null,
  requireSingleDetectionForSuppression?: boolean,
  detectionCount?: number,
): boolean {
  // Only source entities can be suppressed
  if (entityCategory !== 'source') {
    return false;
  }

  // Validate we have required alert context
  const validation = validateSuppressionContext(detectionData);
  if (!validation.isValid) {
    return false;
  }

  // Apply single detection requirement if specified
  if (requireSingleDetectionForSuppression && detectionCount !== 1) {
    return false;
  }

  return true;
}
